import { RingBuffer } from './ring-buffer';
import type { ConsoleEntry, StackFrame } from './types';

const SMART_SAMPLING_WINDOW_MS = 30_000;

type ConsoleLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_MAP: Record<string, ConsoleLevel> = {
  log: 'info',
  info: 'info',
  warn: 'warn',
  error: 'error',
  debug: 'debug',
};

export function parseStackFrames(stack: string | undefined): StackFrame[] {
  if (!stack) return [];
  const lines = stack.split('\n');
  const frames: StackFrame[] = [];

  for (const line of lines) {
    const chromeMatch = line.match(/^\s+at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)$/);
    if (chromeMatch) {
      frames.push({
        fn: chromeMatch[1],
        file: chromeMatch[2],
        line: parseInt(chromeMatch[3], 10),
        column: parseInt(chromeMatch[4], 10),
        in_app: !chromeMatch[2].includes('node_modules'),
      });
      continue;
    }
    const chromeNoFnMatch = line.match(/^\s+at\s+(.+?):(\d+):(\d+)$/);
    if (chromeNoFnMatch) {
      frames.push({
        fn: '<anonymous>',
        file: chromeNoFnMatch[1],
        line: parseInt(chromeNoFnMatch[2], 10),
        column: parseInt(chromeNoFnMatch[3], 10),
        in_app: !chromeNoFnMatch[1].includes('node_modules'),
      });
      continue;
    }
    const firefoxMatch = line.match(/^(.+?)@(.+?):(\d+):(\d+)$/);
    if (firefoxMatch) {
      frames.push({
        fn: firefoxMatch[1] || '<anonymous>',
        file: firefoxMatch[2],
        line: parseInt(firefoxMatch[3], 10),
        column: parseInt(firefoxMatch[4], 10),
        in_app: !firefoxMatch[2].includes('node_modules'),
      });
    }
  }
  return frames;
}

export class ConsoleBuffer {
  private buffer: RingBuffer<ConsoleEntry>;
  private smartSampling: boolean;
  private lastErrorTs = 0;
  private originals: Record<string, (...args: any[]) => void> = {};
  private installed = false;

  constructor(bufferSize = 200, smartSampling = true) {
    this.buffer = new RingBuffer(bufferSize);
    this.smartSampling = smartSampling;
  }

  install(): void {
    if (this.installed) return;
    this.installed = true;

    for (const method of ['log', 'info', 'warn', 'error', 'debug'] as const) {
      this.originals[method] = console[method].bind(console);
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      console[method] = function (...args: any[]) {
        self.capture(method, args);
        self.originals[method](...args);
      };
    }
  }

  private capture(method: string, args: any[]): void {
    const level = LEVEL_MAP[method] ?? 'info';
    const now = Date.now();

    if (level === 'error' || level === 'warn') {
      this.lastErrorTs = now;
    }

    if (this.smartSampling && (level === 'info' || level === 'debug')) {
      if (now - this.lastErrorTs > SMART_SAMPLING_WINDOW_MS) {
        return;
      }
    }

    const message = args
      .map((a) => {
        if (typeof a === 'string') return a;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(' ');

    const entry: ConsoleEntry = {
      ts: new Date(now).toISOString(),
      level,
      message: message.slice(0, 2000),
    };

    if (level === 'error') {
      const errorArg = args.find((a) => a instanceof Error);
      if (errorArg) {
        entry.stack_frames = parseStackFrames(errorArg.stack);
      } else {
        entry.stack_frames = parseStackFrames(new Error().stack);
      }
    }

    this.buffer.push(entry);
  }

  pushEntry(entry: ConsoleEntry): void {
    this.buffer.push(entry);
  }

  getAll(): ConsoleEntry[] {
    return this.buffer.getAll();
  }

  clear(): void {
    this.buffer.clear();
  }

  destroy(): void {
    if (!this.installed) return;
    for (const method of ['log', 'info', 'warn', 'error', 'debug'] as const) {
      if (this.originals[method]) {
        console[method] = this.originals[method];
      }
    }
    this.originals = {};
    this.installed = false;
  }
}
